import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, BadgeEuro } from "lucide-react";
import { Reveal } from "@/components/ScrollReveal";
import { TIER_LABELS } from "@/lib/cursusMeta";

export default function CursusParcours({ formations = [] }) {
    const ordered = [...formations].sort((a, b) => (a.order || 0) - (b.order || 0));
    return (
        <section
            id="parcours"
            data-testid="cursus-parcours"
            className="relative overflow-hidden bg-brand-ink py-28 text-white md:py-40"
        >
            <div className="absolute inset-0 grain pointer-events-none opacity-25" />
            <div className="relative mx-auto max-w-7xl px-6">
                <Reveal>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-orange">
                        Le parcours
                    </span>
                    <h2 className="mt-5 max-w-4xl font-display font-normal leading-[0.9] tracking-tightest text-[44px] sm:text-6xl lg:text-[96px]">
                        Trois jours, <em className="italic text-brand-orange">trois paliers.</em>
                    </h2>
                    <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/65">
                        Trois formations complémentaires. Sept heures chacune. Une montée en puissance continue,
                        des fondamentaux à l'expertise.
                    </p>
                </Reveal>

                <div className="relative mt-20">
                    {/* Vertical timeline line (desktop) */}
                    <motion.div
                        aria-hidden="true"
                        initial={{ scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true, margin: "-20%" }}
                        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute left-8 top-0 hidden h-full w-[2px] origin-top bg-gradient-to-b from-brand-orange via-brand-orange/50 to-transparent md:block"
                    />

                    <div className="space-y-24 md:space-y-28">
                        {ordered.map((f, idx) => (
                            <motion.article
                                key={f.id || f.slug}
                                initial={{ opacity: 0, y: 60 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-15%" }}
                                transition={{ duration: 0.95, delay: idx * 0.12, ease: [0.22, 1, 0.36, 1] }}
                                className="relative md:pl-24"
                                data-testid={`parcours-formation-${idx + 1}`}
                            >
                                {/* Giant numeral watermark */}
                                <motion.div
                                    aria-hidden="true"
                                    initial={{ opacity: 0, scale: 0.7 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true, margin: "-25%" }}
                                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                                    className="pointer-events-none absolute -right-8 -top-20 hidden font-display italic text-brand-orange/[0.08] md:block"
                                    style={{ fontSize: "180px", lineHeight: 1, letterSpacing: "-0.04em" }}
                                >
                                    0{idx + 1}
                                </motion.div>

                                {/* Timeline dot */}
                                <div
                                    aria-hidden="true"
                                    className="absolute left-[26px] top-2 hidden h-4 w-4 -translate-x-1/2 rounded-full bg-brand-orange shadow-[0_0_0_6px_rgba(232,135,60,0.18)] md:block"
                                />

                                <div className="flex flex-col gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-brand-orange">
                                    <span>Palier 0{idx + 1} · {TIER_LABELS[idx] || "Formation"}</span>
                                </div>
                                <h3 className="mt-4 font-display text-3xl leading-tight text-white md:text-5xl">
                                    {f.name}
                                </h3>
                                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/55">
                                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-brand-orange" /> {f.duration_hours || 7}h</span>
                                    <span className="inline-flex items-center gap-1.5"><BadgeEuro className="h-3.5 w-3.5 text-brand-orange" /> {f.price_eur || 390}&nbsp;€</span>
                                </div>

                                <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/70">
                                    {f.short_description}
                                </p>

                                {/* Objectives */}
                                {f.objectives && f.objectives.length > 0 && (
                                    <ul className="mt-8 grid gap-2 md:grid-cols-2">
                                        {f.objectives.slice(0, 4).map((obj) => (
                                            <li key={obj} className="flex gap-3 text-sm leading-relaxed text-white/75">
                                                <span className="text-brand-orange">→</span>
                                                <span>{obj}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* Modules preview */}
                                {f.program_modules && f.program_modules.length > 0 && (
                                    <div className="mt-8 grid gap-3 md:grid-cols-3">
                                        {f.program_modules.slice(0, 3).map((m, mi) => (
                                            <div
                                                key={`${f.slug}-${mi}`}
                                                className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-brand-orange/30"
                                            >
                                                <div className="font-display text-[10px] uppercase tracking-[0.28em] text-brand-orange">
                                                    Module {mi + 1} · {m.duration}
                                                </div>
                                                <div className="mt-3 font-display text-base leading-tight text-white">
                                                    {m.title}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <Link
                                    to={`/formations/${f.slug}`}
                                    data-testid={`parcours-link-${f.slug}`}
                                    className="group mt-10 inline-flex items-center gap-2 border-b border-brand-orange/40 pb-1 text-sm font-semibold text-brand-orange transition-all hover:border-brand-orange"
                                >
                                    Voir le programme détaillé
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
